// Only types are imported statically; the library itself is loaded on demand in loadWorkbook.
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { CellValue, Workbook } from 'exceljs';

/**
 * Reads uploaded .xlsx files into rows of cell text, and those rows into named columns. This module
 * is the only place that knows the spreadsheet file format. Used by SpreadsheetUpload, through
 * which the base rates page creates rates.
 */

export interface SpreadsheetRow {
  // 1-based row number as shown in the spreadsheet.
  rowNumber: number;
  // Cell text by column position; empty cells are ''.
  cells: string[];
}

const UNREADABLE_MESSAGE = 'The file could not be read.';
const NOT_XLSX_MESSAGE = 'The file is not a readable .xlsx workbook.';
const NO_WORKSHEET_MESSAGE = 'The workbook has no worksheets.';
const READER_UNAVAILABLE_MESSAGE =
  'The spreadsheet reader could not be loaded. Reload the page and try again.';

// Arithmetic in a spreadsheet can leave binary noise such as 0.30000000000000004; Excel shows 15 digits.
const SIGNIFICANT_DIGITS = 15;

/** A spreadsheet that could not be read; the message is shown to the user as is. */
export class SpreadsheetError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SpreadsheetError';
  }
}

const toArrayBuffer = (file: Blob): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => resolve(reader.result as ArrayBuffer);
    reader.onerror = (): void => reject(new SpreadsheetError(UNREADABLE_MESSAGE));
    reader.readAsArrayBuffer(file);
  });

const cellText = (value: CellValue): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'number') {
    return String(Number(value.toPrecision(SIGNIFICANT_DIGITS)));
  }
  if (typeof value === 'string' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if ('richText' in value) {
    return value.richText.map(run => run.text).join('');
  }
  if ('formula' in value || 'sharedFormula' in value) {
    return cellText(value.result);
  }
  if ('hyperlink' in value) {
    return cellText(value.text);
  }
  if ('error' in value) {
    return value.error;
  }
  return '';
};

const loadExcelJS = async (): Promise<typeof import('exceljs')> => {
  try {
    return await import('exceljs');
  } catch (error) {
    // The chunk can fail to download, e.g. offline or after a deploy replaced it while the page was open.
    throw new SpreadsheetError(READER_UNAVAILABLE_MESSAGE);
  }
};

const loadWorkbook = async (buffer: ArrayBuffer): Promise<Workbook> => {
  const ExcelJS = await loadExcelJS();
  const workbook = new ExcelJS.Workbook();
  try {
    await workbook.xlsx.load(buffer);
  } catch (error) {
    throw new SpreadsheetError(NOT_XLSX_MESSAGE);
  }
  return workbook;
};

/** Returns the non-empty rows of the first worksheet. Rejects with a SpreadsheetError. */
export const readSpreadsheet = async (file: Blob | ArrayBuffer): Promise<SpreadsheetRow[]> => {
  const buffer = file instanceof Blob ? await toArrayBuffer(file) : file;
  const worksheet = (await loadWorkbook(buffer)).worksheets[0];
  if (!worksheet) {
    throw new SpreadsheetError(NO_WORKSHEET_MESSAGE);
  }

  const rows: SpreadsheetRow[] = [];
  worksheet.eachRow((row, rowNumber) => {
    // row.values is indexed by column number, so index 0 is always empty.
    const cells = Array.from((row.values as CellValue[]).slice(1), cellText);
    if (cells.some(text => text.trim() !== '')) {
      rows.push({ rowNumber, cells });
    }
  });
  return rows;
};

export interface ParsedSheet<F extends string> {
  // Trimmed cell text by column, one entry per data row that is not blank in every column.
  records: Record<F, string>[];
  // Spreadsheet row number of each entry in `records`.
  rowNumbers: number[];
  missingColumns: F[];
}

/**
 * Reads `columns` from sheet rows whose first row names them, matched regardless of case and
 * surrounding spaces; other columns are ignored. No rows are read while a column is missing.
 */
export function parseSheet<F extends string>(
  sheetRows: SpreadsheetRow[],
  columns: readonly F[]
): ParsedSheet<F> {
  const [header, ...dataRows] = sheetRows;
  const columnByName = new Map<string, F>(columns.map(column => [column.toLowerCase(), column]));
  const positionByColumn = new Map<F, number>();
  (header ? header.cells : []).forEach((name, position) => {
    const column = columnByName.get(name.trim().toLowerCase());
    if (column !== undefined && !positionByColumn.has(column)) {
      positionByColumn.set(column, position);
    }
  });
  const missingColumns = columns.filter(column => !positionByColumn.has(column));
  if (missingColumns.length > 0) {
    return { records: [], rowNumbers: [], missingColumns };
  }

  const toRecord = (sheetRow: SpreadsheetRow): Record<F, string> => {
    const record = {} as Record<F, string>;
    columns.forEach(column => {
      record[column] = (sheetRow.cells[positionByColumn.get(column) as number] || '').trim();
    });
    return record;
  };
  const filled = dataRows
    .map(sheetRow => ({ rowNumber: sheetRow.rowNumber, record: toRecord(sheetRow) }))
    .filter(({ record }) => columns.some(column => record[column] !== ''));
  return {
    records: filled.map(({ record }) => record),
    rowNumbers: filled.map(({ rowNumber }) => rowNumber),
    missingColumns,
  };
}
